import { useState, useEffect } from 'react';
import '../styles/modal.css';
import { BACKEND_URL } from '../config';

interface GroupSettingsModalProps {
  conversation: {
    id: string;
    name: string;
    avatarEmoji: string;
    avatarColor: string;
    participants: string[];
  };
  sessionPseudo: string;
  onClose: () => void;
  onSave: (settings: { name?: string; avatarEmoji?: string; avatarColor?: string }) => void;
  onAddMember: (pseudo: string) => void;
  onRemoveMember: (pseudo: string) => void;
  onDeleteGroup: () => void;
}

// Palette de couleurs pour les avatars de groupe
const AVATAR_COLORS = [
  '#128c7e', // Vert WhatsApp
  '#00a884', // Vert clair
  '#25d366', // Vert vif
  '#075e54', // Vert foncé
  '#34b7f1', // Bleu clair
  '#2196f3', // Bleu
  '#3f51b5', // Indigo
  '#673ab7', // Violet
  '#9c27b0', // Pourpre
  '#e91e63', // Rose
  '#f44336', // Rouge
  '#ff5722', // Orange foncé
  '#ff9800', // Orange
  '#ffc107', // Ambre
  '#795548', // Marron
  '#607d8b', // Gris bleu
];

// Emojis classiques pour les avatars de groupe
const AVATAR_EMOJIS = [
  // Visages
  '😀', '😎', '🤩', '🥳', '😇', '🤗', '🙂', '😊',
  // Coeurs et symboles
  '❤️', '💙', '💚', '💛', '💜', '🧡', '🖤', '🤍',
  // Animaux
  '🐶', '🐱', '🐼', '🦁', '🐯', '🦊', '🐰', '🐻',
  '🐸', '🐵', '🦄', '🐝', '🦋', '🐢', '🐬', '🦅',
  // Nature
  '🌸', '🌺', '🌻', '🌹', '🌷', '🍀', '🌲', '🌴',
  '⭐', '🌙', '☀️', '🌈', '❄️', '🔥', '💧', '⚡',
  // Sport & loisirs
  '⚽', '🏀', '🎾', '🎱', '🎯', '🎮', '🎲', '🎸',
  '🎤', '🎬', '📚', '✈️', '🚀', '⛵', '🏔️', '🏖️',
  // Nourriture
  '🍕', '🍔', '🍟', '🍩', '🍪', '🎂', '🍰', '🍫',
  '☕', '🍺', '🍷', '🥤', '🍎', '🍓', '🍉', '🍇',
  // Objets
  '💎', '👑', '🎁', '🏆', '🔔', '💡', '📷', '🎨',
  '🔮', '🧲', '💰', '🎈', '🎉', '🎊', '🪄', '✨',
  // Personnes et groupes
  '👥', '👨‍👩‍👧‍👦', '👯', '🤝', '💪', '🙌', '👏', '✌️',
];

export default function GroupSettingsModal({
  conversation,
  sessionPseudo,
  onClose,
  onSave,
  onAddMember,
  onRemoveMember,
  onDeleteGroup,
}: GroupSettingsModalProps) {
  const [name, setName] = useState(conversation.name);
  const [avatarEmoji, setAvatarEmoji] = useState(conversation.avatarEmoji);
  const [avatarColor, setAvatarColor] = useState(conversation.avatarColor);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [newMemberPseudo, setNewMemberPseudo] = useState('');
  const [searchResults, setSearchResults] = useState<Array<{ pseudo: string; pseudoDisplay: string }>>([]);
  const [confirmDelete, setConfirmDelete] = useState(false);

  // Recherche de joueurs pour ajouter un membre
  useEffect(() => {
    if (newMemberPseudo.length < 2) {
      setSearchResults([]);
      return;
    }

    const timer = setTimeout(async () => {
      try {
        const response = await fetch(
          `${BACKEND_URL}/api/players/search?q=${encodeURIComponent(newMemberPseudo)}`
        );
        if (response.ok) {
          const data = await response.json();
          // Filtrer les membres déjà présents
          const filtered = (data.players || []).filter(
            (p: { pseudo: string }) => 
              !conversation.participants.some(
                m => m.toLowerCase() === p.pseudo.toLowerCase()
              )
          );
          setSearchResults(filtered.slice(0, 5));
        }
      } catch (err) {
        console.error('[GroupSettings] Erreur recherche:', err);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [newMemberPseudo, conversation.participants]);

  const handleSave = () => {
    const changes: { name?: string; avatarEmoji?: string; avatarColor?: string } = {};
    
    if (name !== conversation.name) changes.name = name;
    if (avatarEmoji !== conversation.avatarEmoji) changes.avatarEmoji = avatarEmoji;
    if (avatarColor !== conversation.avatarColor) changes.avatarColor = avatarColor;
    
    if (Object.keys(changes).length > 0) {
      onSave(changes);
    }
    onClose();
  };

  const handleAddMember = (pseudo: string) => {
    onAddMember(pseudo);
    setNewMemberPseudo('');
    setSearchResults([]);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content group-settings" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Paramètres du groupe</h2>
          <button className="close-button" onClick={onClose}>✕</button>
        </div>

        <div className="modal-body">
          {/* Avatar du groupe */}
          <div className="settings-section">
            <h3>Avatar du groupe</h3>
            <div className="avatar-preview-container">
              <div 
                className="avatar-preview large"
                style={{ backgroundColor: avatarColor }}
              >
                {avatarEmoji}
              </div>
              <button 
                className="change-emoji-btn"
                onClick={() => setShowEmojiPicker(!showEmojiPicker)}
              >
                {showEmojiPicker ? 'Fermer' : 'Changer l\'emoji'}
              </button>
            </div>

            {showEmojiPicker && (
              <div className="emoji-picker">
                <div className="emoji-grid">
                  {AVATAR_EMOJIS.map((emoji, index) => (
                    <button
                      key={index}
                      className={`emoji-option ${avatarEmoji === emoji ? 'selected' : ''}`}
                      onClick={() => setAvatarEmoji(emoji)}
                    >
                      {emoji}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <h4>Couleur de fond</h4>
            <div className="color-picker">
              {AVATAR_COLORS.map((color, index) => (
                <button
                  key={index}
                  className={`color-option ${avatarColor === color ? 'selected' : ''}`}
                  style={{ backgroundColor: color }}
                  onClick={() => setAvatarColor(color)}
                />
              ))}
            </div>
          </div>

          {/* Nom du groupe */}
          <div className="settings-section">
            <h3>Nom du groupe</h3>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Nom du groupe"
              maxLength={50}
              className="group-name-input"
            />
          </div>

          {/* Membres */}
          <div className="settings-section">
            <h3>Membres ({conversation.participants.length})</h3>
            <div className="members-list">
              {conversation.participants.map((member) => (
                <div key={member} className="member-item">
                  <span className="member-name">
                    {member}
                    {member.toLowerCase() === sessionPseudo.toLowerCase() && ' (vous)'}
                  </span>
                  {member.toLowerCase() !== sessionPseudo.toLowerCase() && (
                    <button 
                      className="remove-member-btn"
                      onClick={() => onRemoveMember(member)}
                      title="Retirer du groupe"
                    >
                      ✕
                    </button>
                  )}
                </div>
              ))}
            </div>

            <div className="add-member-section">
              <h4>Ajouter un membre</h4>
              <input
                type="text"
                value={newMemberPseudo}
                onChange={(e) => setNewMemberPseudo(e.target.value)}
                placeholder="Rechercher un joueur..."
                className="search-input"
              />
              {searchResults.length > 0 && (
                <div className="search-results">
                  {searchResults.map((player) => (
                    <div 
                      key={player.pseudo}
                      className="search-result-item"
                      onClick={() => handleAddMember(player.pseudo)}
                    >
                      {player.pseudoDisplay || player.pseudo}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Zone de danger */}
          <div className="settings-section danger-zone">
            <h3>Zone de danger</h3>
            {!confirmDelete ? (
              <button 
                className="delete-group-btn"
                onClick={() => setConfirmDelete(true)}
              >
                🗑️ Supprimer le groupe
              </button>
            ) : (
              <div className="confirm-delete">
                <p>Êtes-vous sûr ? Cette action est irréversible.</p>
                <div className="confirm-buttons">
                  <button 
                    className="btn-cancel"
                    onClick={() => setConfirmDelete(false)}
                  >
                    Annuler
                  </button>
                  <button 
                    className="btn-confirm-delete"
                    onClick={onDeleteGroup}
                  >
                    Confirmer la suppression
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="modal-footer">
          <button className="btn-secondary" onClick={onClose}>Annuler</button>
          <button className="btn-primary" onClick={handleSave}>Enregistrer</button>
        </div>
      </div>
    </div>
  );
}
